import 'components/discovery/display-options-toolbar/display-options-toolbar.js'
import 'components/discovery/display-options-toolbar/sort-modes/sort-modes.js'
import { CSSResultGroup, html, LitElement, nothing, PropertyValues } from 'lit'
import { customElement, state, property } from 'lit/decorators.js'
import { style } from './plp-grid.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { SortModes } from 'components/discovery/shared/search/typing/search-results.js'
import { PlpFiltersSelectedFiltersEvent } from 'components/discovery/plp/filters/plp-filters/events.js'
import { CombinedLayoutConfig, CSCBaseProductTile, ProductTileFooterResizedEvent, SimpleLayoutCfg, SimpleWidgetCfg, tileConfigConverter, toInternalProduct } from 'components/shared/base-product-tile/base-product-tile.js'
import type { InternalProduct } from 'components/discovery/product/tile-parts/tile-parts-typing.js'
import type { DynamicSectionCfg, ImageFlowZoneCfg } from 'components/shared/base-product-tile/base-product-tile-types.js'
import { resolveLayoutConfig } from 'components/shared/base-product-tile/layout-config.js'
import { breakpoints } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'
import { getCookie, setCookie } from '@cloudsuite/mosaic-shared/scripts/cookie.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import 'components/discovery/display-options-toolbar/search-results-number/search-results-number.js'
import 'components/discovery/display-options-toolbar/display-modes/display-modes.js'


interface Product {
  id: string
  name: string
  [key: string]: any
}

interface ApiResponse {
  products: Product[]
  total_found: number
  paginator: {
    num_pages: number
    page: number
    page_size: number
    has_prev: boolean
    has_next: boolean
    first_page_api_link: string
    prev_page_api_link: string | null
    next_page_api_link: string | null
    last_page_api_link: string
  }
  sort_modes: SortModes[]
  [key: string]: any
}

type DisplayMode = 'grid' | 'list'

export const plpGridHiddenFields = ['results-label', 'results-number-selector', 'display-modes'] as const

@customElement('csc-plp-grid')
export class CSCPlpGrid extends HiddenFieldsMixin(LitElement, plpGridHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, style]

  private hasInitiallyLoaded = false

  private lastFilterUrl: string | undefined = undefined

  private preservePageOnNextFilterEvent = false

  private didIndependentInitialFetch = false

  private hasReceivedFilterEvent = false

  private static readonly COOKIE_KEY_DESKTOP = 'cs_display_mode_desktop'
  private static readonly COOKIE_KEY_MOBILE = 'cs_display_mode_mobile'
  private static readonly COOKIE_KEY_MAX_RESULTS = 'cs_maxresult'

  private static readonly DEFAULT_FEATURES = ['stock', 'prices', 'warehouses'] as const

  @state()
  private isMobile = false

  private mediaQuery: MediaQueryList | undefined

  private onMediaChange = (e: MediaQueryListEvent) => {
    this.isMobile = e.matches
    this.displayMode = this.loadDisplayModeForDevice()
  }

  @state()
  private apiResponse: ApiResponse | null = null

  private tileProducts: InternalProduct[] = []

  private effectiveLayoutConfig: SimpleLayoutCfg | null = null

  @state()
  private isLoading = false

  @state()
  private error: string | null = null

  @state()
  private displayMode: DisplayMode = 'grid'

  @state()
  private resultsPerPage = 24

  /** @deprecated Use `hidden-fields="results-number-selector"` instead. */
  @property({ type: String, attribute: 'results-number-selector-display' })
  set resultsNumberSelectorDisplay(value: string) {
    this.setLegacyVisibility('results-number-selector-display', 'results-number-selector', value === 'show')
  }

  get resultsNumberSelectorDisplay(): 'show' | 'hide' {
    return this.isHidden('results-number-selector') ? 'hide' : 'show'
  }

  /** @deprecated Use `hidden-fields="display-modes"` instead. */
  @property({ type: String, attribute: 'display-modes-display' })
  set displayModesDisplay(value: string) {
    this.setLegacyVisibility('display-modes-display', 'display-modes', value === 'show')
  }

  get displayModesDisplay(): 'show' | 'hide' {
    return this.isHidden('display-modes') ? 'hide' : 'show'
  }

  /** @deprecated Use `hidden-fields="display-modes"` instead. */
  @property({ type: String, attribute: 'grid-toggle-display' })
  set gridToggleDisplay(value: string) {
    this.setLegacyVisibility('grid-toggle-display', 'display-modes', value === 'show')
  }

  get gridToggleDisplay(): 'show' | 'hide' {
    return this.isHidden('display-modes') ? 'hide' : 'show'
  }

  /** @deprecated Use `hidden-fields="results-label"` instead. */
  @property({ type: String, attribute: 'results-label-display' })
  set resultsLabelDisplay(value: string) {
    this.setLegacyVisibility('results-label-display', 'results-label', value === 'show')
  }

  get resultsLabelDisplay(): 'show' | 'hide' {
    return this.isHidden('results-label') ? 'hide' : 'show'
  }

  @property({ type: String, attribute: 'default-display-mode-desktop' })
  defaultDisplayModeDesktop: DisplayMode = 'grid'

  @property({ type: String, attribute: 'default-display-mode-mobile' })
  defaultDisplayModeMobile: DisplayMode = 'list'

  /** @deprecated Set `--cse-group-row-gap` in CSS instead. */
  @property({ type: String, attribute: 'group-row-gap' })
  set groupRowGap(_value: string) {
    console.warn('<csc-plp-grid> "group-row-gap" is deprecated, set "--cse-group-row-gap" in CSS instead.')
  }

  get groupRowGap() {
    return '08'
  }

  @property({ type: String, attribute: 'api-base-url' })
  apiBaseUrl = ''

  @property({ type: String, attribute: 'api-full-url' })
  apiFullUrl = ''

  @property({ type: String, attribute: 'tree-id' })
  treeId?: string

  @property({ type: String, attribute: 'brand-id' })
  brandId?: string

  @property({ type: String, attribute: 'tag-id' })
  tagId?: string

  @property({ type: String, attribute: 'search-term' })
  searchTerm?: string

  @property({ type: Number, attribute: 'tiles-per-row' })
  tilesPerRow = 4

  @property({ type: Number, attribute: 'tiles-per-row-mobile' })
  tilesPerRowMobile = 1

  @property({ type: Number, attribute: 'banner-start' })
  bannerStart: number = 0

  @property({ type: Number, attribute: 'banner-width' })
  bannerWidth: number = 1

  @property({ type: Boolean, attribute: 'scroll-to-top' })
  scrollToTop: boolean = false

  @state()
  private currentPage = 1

  private get bannerInsertIndex(): number {
    return Math.max(0, this.bannerStart)
  }

  private get bannerSpan(): number {
    if (this.effectiveDisplayMode !== 'grid') {
      return this.tilesPerRowEffective
    }

    return Math.min(this.bannerWidth, this.tilesPerRowEffective)
  }

  private renderBannerBetweenProducts() {
    const fullWidth = this.effectiveDisplayMode !== 'grid'
    return html`
      <div class="banner-between-products ${fullWidth ? 'banner--full' : `banner--width-${this.bannerSpan}`}" style="${fullWidth ? '' : `grid-column: span ${this.bannerSpan};`}">
        <slot class="banner-between-products__content"></slot>
      </div>
    `
  }

  @state()
  private currentSortMode = ''

  @property({ type: Object, attribute: 'tile-config', converter: tileConfigConverter('csc-plp-grid') })
  tileConfig: CombinedLayoutConfig | SimpleLayoutCfg | null = {
    image: {
      'bottom-left': [{ type: 'csc-product-tile-tags' }],
      'top-right': [{ type: 'csc-product-tile-add-to-order-template' }]
    },
    productInfo: {
      layout: [['zone-1'], ['zone-2'], ['zone-3']],
      zones: {
        'zone-1': {
          alignItems: 'flex-start',
          widgets: [
            { type: 'csc-product-tile-title' },
            { type: 'csc-brand' }
          ]
        },
        'zone-2': {
          alignItems: 'flex-start',
          widgets: [
            { type: 'csc-price' },
            { type: 'csc-product-tile-product-code' },
            { type: 'csc-stock-indication' },
            { type: 'csc-product-tile-variant-indication', showWhen: [{ condition: 'variant_grouping', is: true }] },
            { type: 'csc-product-tile-variants' }
          ]
        },
        'zone-3': {
          alignItems: 'flex-start',
          widgets: [
            { type: 'csc-featured-attributes' },
            { type: 'csc-plp-cta-button' }
          ]
        }
      }
    },
    orderActions: {
      layout: [['zone-1']],
      zones: {
        'zone-1': {
          alignItems: 'flex-end',
          widgets: [
            { type: 'csc-order-form', showWhen: [{ condition: 'is_configurator_product', is: false }, { condition: 'orderable', is: true }, { condition: 'variant_grouping', is: false }] },
            { type: 'csc-plp-cta-button', properties: { type: 'configurator-product' }, showWhen: [{ condition: 'is_configurator_product', is: true }] },
            { type: 'csc-plp-cta-button', properties: { type: 'choose-variant' }, showWhen: [{ condition: 'variant_grouping', is: true }, { condition: 'orderable', is: true }, { condition: 'is_configurator_product', is: false }] }
          ]
        }
      }
    }
  }

  @property({ type: Number, attribute: 'variants-display-limit' })
  variantsDisplayLimit: number = 5

  /** @deprecated Use `variants-display-limit` instead. */
  @property({ type: Number, attribute: 'max-variants-amount' })
  set maxVariantsAmount(value: number) {
    console.warn('<csc-plp-grid> "max-variants-amount" is deprecated, use "variants-display-limit" instead.')
    this.variantsDisplayLimit = value
  }

  get maxVariantsAmount() {
    return this.variantsDisplayLimit
  }

  @property({ type: String })
  locale: string = 'en-US'

  @property({ type: Object, attribute: 'api-params' })
  apiParams: Record<string, string | string[]> = { }

  @property({ type: String, attribute: 'sort-mode' })
  sortMode = ''

  private hasActiveUrlFilters(): boolean {
    if (typeof window === 'undefined') return false
    return new URLSearchParams(window.location.search).has('v')
  }

  private filterUrlHasRefinements(filterUrl: string | undefined): boolean {
    if (!filterUrl) return false
    try {
      const url = new URL(filterUrl, window.location.origin)
      return new URLSearchParams(url.search).has('v')
    } catch {
      return false
    }
  }

  private hasMarketingContent() {
    // Check light DOM children to see if any marketing content was provided
    return (this.children?.length ?? 0) > 0
  }

  private footerRowHeights = new Map<Element, number[]>()
  private footerHeightSyncHandle: number | null = null

  private handleFooterResized = (e: ProductTileFooterResizedEvent) => {
    const tile = e.composedPath().find(
      (node): node is HTMLElement => node instanceof HTMLElement && node.tagName === 'CSC-BASE-PRODUCT-TILE'
    )
    if (!tile) return

    this.footerRowHeights.set(tile, e.heights)
    this.scheduleFooterHeightSync()
  }

  private scheduleFooterHeightSync() {
    if (this.footerHeightSyncHandle !== null) return
    this.footerHeightSyncHandle = requestAnimationFrame(() => {
      this.footerHeightSyncHandle = null
      this.applyFooterHeightMax()
    })
  }

  private applyFooterHeightMax() {
    const allRows = [...this.footerRowHeights.values()]
    if (!allRows.length) return

    const rowCount = Math.max(...allRows.map(rows => rows.length))
    const maxPerRow = Array.from({ length: rowCount }, (_, i) =>
      Math.ceil(Math.max(...allRows.map(rows => rows[i] ?? 0)))
    )
    for (const tile of this.footerRowHeights.keys()) {
      (tile as CSCBaseProductTile).footerRowMinHeights = maxPerRow
    }
  }

  private applyContextParams(params: URLSearchParams) {
    if (this.treeId) {
      params.set('tree_id', this.treeId)
    }
    if (this.brandId) {
      params.set('brand_id', this.brandId)
    }
    if (this.tagId) {
      params.set('tag_id', this.tagId)
    }
    if (this.searchTerm) {
      params.set('search_term', this.searchTerm)
    }
    if (this.sortMode) {
      params.set('sort_mode', this.sortMode)
    }
  }

  /**
   * Multi-value API params (e.g. features) must be repeated keys:
   * `features=prices&features=stock`, not `features=prices,stock`.
   */
  private normalizeParamValues(value: string | string[]): string[] {
    if (Array.isArray(value)) {
      return value.map(String).map(v => v.trim()).filter(Boolean)
    }
    if (value.includes(',')) {
      return value.split(',').map(v => v.trim()).filter(Boolean)
    }
    return [value]
  }

  private applyApiParams(params: URLSearchParams) {
    Object.entries(this.apiParams ?? {}).forEach(([key, value]) => {
      const values = this.normalizeParamValues(value)
      params.delete(key)
      values.forEach(v => params.append(key, v))
    })
  }

  /** Expand any remaining comma-joined multi-value params (e.g. from api-full-url). */
  private expandCommaSeparatedParams(params: URLSearchParams, keys: string[] = ['features']) {
    for (const key of keys) {
      const existing = params.getAll(key)
      if (existing.length !== 1 || !existing[0]?.includes(',')) continue
      const values = this.normalizeParamValues(existing[0])
      params.delete(key)
      values.forEach(v => params.append(key, v))
    }
  }

  private applyDefaultFeatures(params: URLSearchParams) {
    const merged = [...new Set([...CSCPlpGrid.DEFAULT_FEATURES, ...params.getAll('features')])]

    params.delete('features')
    merged.forEach(feature => params.append('features', feature))
  }

  private async fetchProducts(filterUrl?: string) {

    if (this.hasInitiallyLoaded && this.scrollToTop) {
      this.scrollToTopIfNeeded()
    }

    this.isLoading = true
    this.error = null

    try {
      let url: string

      const baseUrl = this.apiBaseUrl || window.location.origin
      const apiUrlObj = new URL(this.apiFullUrl, baseUrl)
      const apiBaseParams = new URLSearchParams(apiUrlObj.search)

      if (filterUrl) {
        // Convert filter URL to products URL with same parameters
        const filterUrlObj = new URL(filterUrl, baseUrl)
        const filterParams = new URLSearchParams(filterUrlObj.search)

        apiBaseParams.forEach((value, key) => {
          if (!filterParams.has(key)) filterParams.set(key, value)
        })

        filterParams.set('page', this.currentPage.toString())
        filterParams.set('page_size', this.effectiveResultsPerPage.toString())

        this.applyContextParams(filterParams)

        if (this.currentSortMode) {
          filterParams.set('sort_mode', this.currentSortMode)
        }

        this.applyApiParams(filterParams)
        this.expandCommaSeparatedParams(filterParams)
        this.applyDefaultFeatures(filterParams)

        url = `${apiUrlObj.origin}${apiUrlObj.pathname}?${filterParams.toString()}`
      } else {
        const params = new URLSearchParams(apiBaseParams)

        params.set('page', this.currentPage.toString())
        params.set('page_size', this.effectiveResultsPerPage.toString())

        this.applyContextParams(params)

        if (this.currentSortMode) {
          params.set('sort_mode', this.currentSortMode)
        }

        // Add any existing URL parameters (filters, favorites, etc.)
        const currentUrlParams = new URLSearchParams(window.location.search)
        currentUrlParams.forEach((value, key) => {
          if (!['tree_id', 'brand_id', 'tag_id', 'page', 'page_size', 'sort_mode', 'search_term'].includes(key)) {
            params.set(key, value)
          }
        })

        this.applyApiParams(params)
        this.expandCommaSeparatedParams(params)
        this.applyDefaultFeatures(params)

        url = `${apiUrlObj.origin}${apiUrlObj.pathname}?${params.toString()}`
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'accept': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.products) {
        data.products = data.products.map((product: any) => {
          if (product.picture) {
            Object.keys(product.picture).forEach(size => {
              if (product.picture[size] && product.picture[size].startsWith('/')) {
                product.picture[size] = `${this.apiBaseUrl}${product.picture[size]}`
              }
            })
          }
          if (product.default_picture) {
            Object.keys(product.default_picture).forEach(size => {
              if (product.default_picture[size] && product.default_picture[size].startsWith('/')) {
                product.default_picture[size] = `${this.apiBaseUrl}${product.default_picture[size]}`
              }
            })
          }
          return product
        })
      }
      this.apiResponse = data
      this.isLoading = false
      this.hasInitiallyLoaded = true
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'An unknown error occurred'
      console.error('<csc-plp-grid> could not load the products:', this.error)
      this.isLoading = false
    }
  }


  private handleSortingChange(sortMode: SortModes) {
    this.currentSortMode = sortMode.id
    this.currentPage = 1
    this.updateBrowserUrl()
    this.fetchProducts()
  }

  private get deviceCookieKey(): string {
    return this.isMobile ? CSCPlpGrid.COOKIE_KEY_MOBILE : CSCPlpGrid.COOKIE_KEY_DESKTOP
  }

  private get deviceDefaultMode(): DisplayMode {
    return this.isMobile ? this.defaultDisplayModeMobile : this.defaultDisplayModeDesktop
  }

  private loadDisplayModeForDevice(): DisplayMode {
    const cookieValue = getCookie(this.deviceCookieKey)
    if (cookieValue && (cookieValue === 'grid' || cookieValue === 'list')) {
      return cookieValue as DisplayMode
    }
    return this.deviceDefaultMode
  }

  private saveDisplayModeForDevice(mode: DisplayMode) {
    setCookie(this.deviceCookieKey, mode)
  }

  private loadResultsPerPage(): number {
    const cookieValue = getCookie(CSCPlpGrid.COOKIE_KEY_MAX_RESULTS)
    const parsed = cookieValue ? parseInt(cookieValue, 10) : undefined
    const validOptions = this.getMaxResultOptions().map(option => option.num)
    if (parsed !== undefined && validOptions.includes(parsed)) {
      return parsed
    }
    return this.resultsPerPage
  }

  private get effectiveDisplayMode(): DisplayMode {
    return this.displayMode
  }

  private applyWidgetProperties(widget: SimpleWidgetCfg): SimpleWidgetCfg {
    const properties: Record<string, any> = { ...(widget.properties ?? {}) }

    if (widget.type === 'csc-product-tile-variants' && this.variantsDisplayLimit !== 5) {
      properties['displayLimit'] = this.variantsDisplayLimit
    }

    return {
      ...widget,
      ...(Object.keys(properties).length ? { properties } : {}),
    }
  }

  private static readonly IMAGE_OVERLAY_PLACES = new Set([
    'top-left', 'top-center', 'top-right',
    'middle-left', 'middle-center', 'middle-right',
    'bottom-left', 'bottom-center', 'bottom-right'
  ])

  private static readonly IMAGE_FLOW_PLACES = new Set(['above', 'below'])

  private applyWidgetPropertiesToImage(image: Record<string, unknown> | undefined): Record<string, unknown> {
    const result: Record<string, unknown> = {}
    for (const place of Object.keys(image ?? {})) {
      const value: unknown = image?.[place]
      if (CSCPlpGrid.IMAGE_FLOW_PLACES.has(place)) {
        const zone = value as ImageFlowZoneCfg | undefined
        result[place] = zone?.widgets ? { ...zone, widgets: zone.widgets.map(w => this.applyWidgetProperties(w)) } : zone
        continue
      }
      if (!CSCPlpGrid.IMAGE_OVERLAY_PLACES.has(place)) {
        result[place] = value
        continue
      }
      if (value == null) continue
      if (!Array.isArray(value)) {
        console.error(
          `csc-plp-grid: tile-config.image.${place} must be an array of widgets, e.g. [{ "type": "csc-price" }] — got ${JSON.stringify(value)} instead. Wrap it in an array. This zone will be skipped until fixed.`
        )
        continue
      }
      result[place] = (value as SimpleWidgetCfg[]).map(w => this.applyWidgetProperties(w))
    }
    return result
  }

  private applyWidgetPropertiesToDynamicSection(section: DynamicSectionCfg | undefined): DynamicSectionCfg {
    if (!section?.zones) return section ?? {}
    const zones = Object.fromEntries(Object.entries(section.zones).map(([zoneId, zone]) => [
      zoneId,
      { ...zone, widgets: (zone.widgets ?? []).map(w => this.applyWidgetProperties(w)) }
    ]))
    return { ...section, zones }
  }

  private getEffectiveLayoutConfig(): SimpleLayoutCfg | null {
    if (!this.tileConfig) return null
    const layout = resolveLayoutConfig(this.tileConfig, this.getEffectiveCardVariant(), this.isMobile)
    return {
      ...layout,
      image: this.applyWidgetPropertiesToImage(layout.image as Record<string, unknown>) as SimpleLayoutCfg['image'],
      productInfo: this.applyWidgetPropertiesToDynamicSection(layout.productInfo),
      orderActions: this.applyWidgetPropertiesToDynamicSection(layout.orderActions)
    }
  }

  private getEffectiveCardVariant(): 'default' | 'wide' | 'inline' {
    if (this.effectiveDisplayMode === 'grid') return 'default'
    return this.isMobile ? 'inline' : 'wide'
  }

  private get effectiveTilesPerRowMobile(): number {
    return this.tilesPerRowMobile === 2 ? 2 : 1
  }

  private get tilesPerRowEffective(): number {
    return this.isMobile ? this.effectiveTilesPerRowMobile : this.tilesPerRow
  }

  private get effectiveResultsPerPage(): number {
    const base = this.resultsPerPage
    // Only adjust for grid display and banner widths > 1
    if (this.effectiveDisplayMode !== 'grid' || this.bannerWidth <= 1) return base

    const tilesPerRow = Math.max(1, this.tilesPerRowEffective)
    const rows = Math.ceil(base / tilesPerRow)
    const reductionPerRow = Math.max(0, this.bannerWidth - 1)
    const reduced = base - rows * reductionPerRow
    return Math.max(1, reduced)
  }

  private handleDisplayModeChange(e: CustomEvent<{ mode: DisplayMode }>) {
    this.displayMode = e.detail.mode
    this.saveDisplayModeForDevice(e.detail.mode)
  }

  private renderSkeletonTiles() {
    const nodes = [] as any[]

    for (let i = 0; i < this.effectiveResultsPerPage; i++) {
      nodes.push(html`
        <csc-base-product-tile
          card-variant="${this.getEffectiveCardVariant()}"
          loading>
        </csc-base-product-tile>
      `)

      if (this.hasMarketingContent() && i === this.bannerInsertIndex) {
        nodes.push(this.renderBannerBetweenProducts())
      }
    }

    return html`${nodes}`
  }

  override connectedCallback() {
    super.connectedCallback()
    const urlParams = new URLSearchParams(window.location.search)
    const sortMode = urlParams.get('sort_mode')
    const page = urlParams.get('page')
    if (sortMode) this.currentSortMode = sortMode
    if (page) {
      this.currentPage = parseInt(page, 10)
      this.preservePageOnNextFilterEvent = true
    }
    document.addEventListener('plp-filters:selected-filters', this.handleSelectedFiltersEvent as EventListener)
    this.mediaQuery = window.matchMedia(`(max-width: ${breakpoints.medium - 1}px)`)
    this.isMobile = this.mediaQuery.matches
    this.mediaQuery.addEventListener('change', this.onMediaChange)
    window.addEventListener('popstate', this.handlePopState)
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener('plp-filters:selected-filters', this.handleSelectedFiltersEvent as EventListener)
    this.mediaQuery?.removeEventListener('change', this.onMediaChange)
    window.removeEventListener('popstate', this.handlePopState)
    if (this.footerHeightSyncHandle !== null) cancelAnimationFrame(this.footerHeightSyncHandle)
  }

  private handlePopState = (): void => {
    const urlParams = new URLSearchParams(window.location.search)
    const sortMode = urlParams.get('sort_mode') ?? ''
    const page = urlParams.get('page') ?? '1'
    this.currentSortMode = sortMode
    this.currentPage = parseInt(page, 10)
    this.preservePageOnNextFilterEvent = true
    this.fetchProducts(this.lastFilterUrl)
  }

  private updateBrowserUrl(): void {
    if (typeof window === 'undefined') return
    const currentParams = new URLSearchParams(window.location.search)

    if (this.currentSortMode) {
      currentParams.set('sort_mode', this.currentSortMode)
    } else {
      currentParams.delete('sort_mode')
    }

    if (this.currentPage > 1) {
      currentParams.set('page', this.currentPage.toString())
    } else {
      currentParams.delete('page')
    }

    const newSearch = currentParams.toString()
    const newQuery = newSearch ? `?${newSearch}` : ''
    const currentQuery = window.location.search || ''
    if (currentQuery !== newQuery) {
      window.history.pushState(null, '', window.location.pathname + newQuery)
    }
  }

  override willUpdate(changedProperties: PropertyValues) {
    super.willUpdate(changedProperties)
    if (changedProperties.has('apiResponse')) {
      this.tileProducts = (this.apiResponse?.products ?? []).map(toInternalProduct)
    }
    if (['tileConfig', 'displayMode', 'isMobile', 'variantsDisplayLimit'].some(key => changedProperties.has(key))) {
      this.effectiveLayoutConfig = this.getEffectiveLayoutConfig()
    }
  }

  override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties)
    if (changedProperties.has('apiResponse')) {
      this.footerRowHeights.clear()
      this.shadowRoot?.querySelectorAll('csc-base-product-tile').forEach(tile => {
        (tile as CSCBaseProductTile).footerRowMinHeights = null
      })
    }
  }

  override firstUpdated() {
    this.displayMode = this.loadDisplayModeForDevice()
    this.resultsPerPage = this.loadResultsPerPage()

    if (!this.hasActiveUrlFilters()) {
      this.didIndependentInitialFetch = true
      this.fetchProducts()
    }
  }


  private handlePageNavigation(e: any) {
    const pageLink = e.pageLink
    if (pageLink) {
      const urlParams = new URLSearchParams(pageLink.split('?')[1])
      const pageNum = urlParams.get('page')
      if (pageNum) {
        this.currentPage = parseInt(pageNum, 10)
      }
      this.updateBrowserUrl()
      this.fetchProducts(this.lastFilterUrl)
    }
  }

  private handleResultsNumberChange(e: CustomEvent<{ number: number }>) {
    this.resultsPerPage = e.detail.number
    this.currentPage = 1 // Reset to first page when page size changes
    this.fetchProducts()
  }

  private scrollToTopIfNeeded() {
    const target = this.parentElement ?? this
    target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  private handleSelectedFiltersEvent = (event: PlpFiltersSelectedFiltersEvent) => {
    const isFirstFilterEvent = !this.hasReceivedFilterEvent
    this.hasReceivedFilterEvent = true

    if (this.preservePageOnNextFilterEvent) {
      this.preservePageOnNextFilterEvent = false
    } else {
      this.currentPage = 1

      const currentParams = new URLSearchParams(window.location.search)
      currentParams.delete('page')
      const newSearch = currentParams.toString()
      const newQuery = newSearch ? `?${newSearch}` : ''
      if ((window.location.search || '') !== newQuery) {
        window.history.replaceState(null, '', window.location.pathname + newQuery)
      }
    }

    this.lastFilterUrl = event.filterUrl || undefined

    // Grid already fetched independently (no filters were active on load)
    if (isFirstFilterEvent && this.didIndependentInitialFetch && !this.filterUrlHasRefinements(this.lastFilterUrl)) {
      return
    }

    if (this.lastFilterUrl) {
      this.fetchProducts(this.lastFilterUrl)
    } else {
      this.fetchProducts()
    }
  }


  private getMaxResultOptions() {
    return [
      { num: 12, selected: this.resultsPerPage === 12 },
      { num: 24, selected: this.resultsPerPage === 24 },
      { num: 48, selected: this.resultsPerPage === 48 },
      { num: 96, selected: this.resultsPerPage === 96 }
    ]
  }

  private renderProductsContent() {
    if (this.isLoading || !this.apiResponse) {
      return this.renderSkeletonTiles()
    }

    if (this.apiResponse.products.length === 0) {
      return html`<p>${t('no_results', { ns: 'csc-plp-grid', default: 'No products found' })}</p>`
    }

    const products = this.tileProducts

    const nodes = [] as any[]

    for (let i = 0; i < products.length; i++) {
      const product = products[i]
      nodes.push(html`
        <csc-base-product-tile
          .product=${product}
          .tileConfig=${this.effectiveLayoutConfig}
          card-variant="${this.getEffectiveCardVariant()}"
          locale="${this.locale}"
          index="${i}"
        ></csc-base-product-tile>
      `)

      if (this.hasMarketingContent() && i === this.bannerInsertIndex) {
        nodes.push(this.renderBannerBetweenProducts())
      }
    }

    return html`${nodes}`
  }

  override render() {
    return html`

      <div class="plp-grid-container">
        <div class="results">
            <cse-group direction="column" nowrap row-gap="08">
              ${this.error ? html`
                <cse-alert variant="error" class="error">
                  ${t('error_title', { ns: 'csc-plp-grid', default: 'Something went wrong' })}
                  <span slot="description">
                    ${t('error_description', { ns: 'csc-plp-grid', default: 'The products could not be loaded. Please try again later.' })}
                  </span>
                </cse-alert>
              ` : nothing}
              <csc-display-options-toolbar ?loading="${this.isLoading || !this.apiResponse}">
                ${!this.isHidden('results-label') ? t('products_result', { ns: 'csc-plp-grid', default: 'Results' }) : nothing}
                <div slot="actions" class="toolbar-actions">
                  <cse-text variant="body-s" class="product-count">
                    ${this.apiResponse?.total_found || this.apiResponse?.products?.length || 0}
                    ${t('amount_of_products_label', { ns: 'csc-plp-grid', default: 'products' })}
                  </cse-text>

                  ${!this.isHidden('results-number-selector') ? html`
                    <csc-search-results-number
                      max-results-options="${JSON.stringify(this.getMaxResultOptions())}"
                      @results-number-changed=${this.handleResultsNumberChange}
                    ></csc-search-results-number>
                  ` : nothing}

                  ${!this.isHidden('display-modes') ? html`
                    <csc-display-modes
                      .value="${this.displayMode}"
                      @display-mode-changed=${this.handleDisplayModeChange}
                    ></csc-display-modes>
                  ` : nothing}
                  ${this.apiResponse?.sort_modes && this.apiResponse?.sort_modes.length > 1 ? html`
                    <csc-sort-modes
                      sort-options="${JSON.stringify(this.apiResponse?.sort_modes || [])}"
                      @sort-option-selected=${(e: CustomEvent<SortModes>) => this.handleSortingChange(e.detail)}
                    ></csc-sort-modes>
                  ` : nothing }
                </div>
              </csc-display-options-toolbar>

              <div
                class="products-grid view-${this.effectiveDisplayMode}"
                style="--tiles-per-row: ${this.tilesPerRow}; --tiles-per-row-mobile: ${this.effectiveTilesPerRowMobile};"
                @csc-product-tile-footer-resized="${this.handleFooterResized}"
              >
                ${this.renderProductsContent()}
              </div>

              ${this.apiResponse?.paginator && !this.isLoading ? html`
                <csc-pagination
                  current-page="${this.apiResponse.paginator.page}"
                  number-of-pages="${this.apiResponse.paginator.num_pages}"
                  first-link="${this.apiResponse.paginator.first_page_api_link}"
                  prev-link="${this.apiResponse.paginator.prev_page_api_link}"
                  next-link="${this.apiResponse.paginator.next_page_api_link}"
                  last-link="${this.apiResponse.paginator.last_page_api_link}"
                  @pagination-on-navigate="${this.handlePageNavigation}"
                ></csc-pagination>
              ` : nothing}
            </cse-group>
          </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-plp-grid': CSCPlpGrid
  }
}
